import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { SubscriptionsDto } from 'components/Admin/organisms/Subscriptions/types';
import { Categories } from 'store/slices/cars/types';
import type { SubscriptionStatus } from 'components/Admin/organisms/Subscriptions/types';

export interface CreateSubscriptionRequest {
  daysCount: number;
  includedCategories: Array<Categories>;
  status: SubscriptionStatus;
  price: number;
}
export enum SubscriptionPackageType {
  '15_DAYS' = '15_DAYS',
  '30_DAYS' = '30_DAYS',
  '45_DAYS' = '45_DAYS',
}
export interface SubscriptionPackage {
  id: string;
  price: number;
  type: SubscriptionPackageType;
  category: Categories;
  includedCategories: Array<Categories>;
  timePlan: number;
}

type InitialState = {
  isPending: boolean;
  items: Array<SubscriptionsDto>;
  selectedSubscription: SubscriptionsDto | null;
  subscriptionPackagesIsPending: boolean;
  subscriptionPackages: Record<Categories, Array<SubscriptionPackage>>;
  selectedPackage: SubscriptionPackage | null;
  isCreationInProgress: boolean;
  cachedSelectedSubscriptions: Record<string, SubscriptionsDto>;
};
const initialState: InitialState = {
  isPending: false,
  items: [],
  selectedSubscription: null,
  subscriptionPackages: {
    [Categories.ELEGANT]: [],
    [Categories.SPORT]: [],
    [Categories.SUPER_SPORT]: [],
    [Categories.EXCLUSIVE]: [],
  },
  selectedPackage: null,
  subscriptionPackagesIsPending: false,
  isCreationInProgress: false,
  cachedSelectedSubscriptions: {},
};

export const subscriptionsSlice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
    resetStore: (state) => {
      return {
        ...state,
        ...initialState,
        subscriptionPackages: state.subscriptionPackages,
        cachedSelectedSubscriptions: state.cachedSelectedSubscriptions,
      };
    },
    setCachedSubscription: (
      state,
      action: PayloadAction<{ key: string; item: SubscriptionsDto }>,
    ) => {
      return {
        ...state,
        cachedSelectedSubscriptions: {
          ...state.cachedSelectedSubscriptions,
          [action.payload.key]: action.payload.item,
        },
      };
    },
    setCreationStatus: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isCreationInProgress: action.payload,
      };
    },
    setSelectedPackage: (
      state,
      action: PayloadAction<SubscriptionPackage | null>,
    ) => {
      return {
        ...state,
        selectedPackage: action.payload,
      };
    },
    setSubscriptionPackagesPendingStatus: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      return {
        ...state,
        subscriptionPackagesIsPending: action.payload,
      };
    },
    setUpdatedPackage: (state, action: PayloadAction<SubscriptionPackage>) => {
      return {
        ...state,
        subscriptionPackages: {
          ...state.subscriptionPackages,
          [action.payload.category]: state.subscriptionPackages[
            action.payload.category
          ].map((item) =>
            item.id === action.payload.id ? action.payload : item,
          ),
        },
      };
    },
    setSubscriptionPackages: (
      state,
      action: PayloadAction<Record<Categories, Array<SubscriptionPackage>>>,
    ) => {
      return {
        ...state,
        subscriptionPackages: action.payload,
      };
    },
    setSelectedSubscription: (
      state,
      action: PayloadAction<SubscriptionsDto | null>,
    ) => {
      return {
        ...state,
        selectedSubscription: action.payload,
      };
    },
    setSubscriptionsPendingStatus: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isPending: action.payload,
      };
    },
    setNewSubscription: (state, action: PayloadAction<SubscriptionsDto>) => {
      return {
        ...state,
        items: state.items.concat(action.payload),
      };
    },
    setSubscriptions: (
      state,
      action: PayloadAction<Array<SubscriptionsDto>>,
    ) => {
      return {
        ...state,
        items: action.payload,
      };
    },
    updateSubscription: (state, action: PayloadAction<SubscriptionsDto>) => {
      return {
        ...state,
        cachedSelectedSubscriptions: {
          ...state.cachedSelectedSubscriptions,
          [action.payload.id]: action.payload,
        },
        items: state.items.map((item) =>
          item.id === action.payload.id ? action.payload : item,
        ),
      };
    },
  },
});
